import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/i18n";
import { api } from "@/lib/api";
import XentraWidget from "@/components/XentraWidget";
import { PromoCard } from "@/components/PromoCard";
import { ArrowRight, ChevronDown, Star, Wifi, KeyRound, Headphones, Coffee, Sparkles, ChefHat } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CountUp } from "@/components/CountUp";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { CursorSpotlight } from "@/components/CursorSpotlight";
import { SavingsCalculator } from "@/components/SavingsCalculator";
import { MagneticButton } from "@/components/MagneticButton";
import { TiltCard } from "@/components/TiltCard";

const DEFAULT_HERO = "https://images.unsplash.com/photo-1630699144035-c0f6311ec482?crop=entropy&cs=srgb&auto=format&fit=crop&q=70&w=1600";
const DEFAULT_GALLERY = [
  "https://images.unsplash.com/photo-1756079664354-34944e001f6d?crop=entropy&cs=srgb&auto=format&fit=crop&q=70&w=700",
  "https://images.unsplash.com/photo-1611094016919-36b65678f3d6?crop=entropy&cs=srgb&auto=format&fit=crop&q=70&w=700",
  "https://images.unsplash.com/photo-1564078516393-cf04bd966897?crop=entropy&cs=srgb&auto=format&fit=crop&q=70&w=700",
  "https://images.unsplash.com/photo-1688646953306-5ec93eab8c06?crop=entropy&cs=srgb&auto=format&fit=crop&q=70&w=700",
];

const TESTIMONIALS = [
  { key: "testi_1", name: "Maria K.", city: "Oberhausen", rating: 5 },
  { key: "testi_2", name: "Thomas B.", city: "Dinslaken", rating: 5 },
  { key: "testi_3", name: "Sandra L.", city: "Oberhausen", rating: 5 },
  { key: "testi_4", name: "Kaur M.", city: "Oberhausen", rating: 5 },
  { key: "testi_5", name: "Julia W.", city: "Dinslaken", rating: 5 },
];

const FEATURES = [
  { icon: Wifi, key: "amenity_wifi", descKey: "std_wifi_d" },
  { icon: KeyRound, key: "amenity_self_checkin", descKey: "std_checkin_d" },
  { icon: Headphones, key: "amenity_support", descKey: "std_support_d" },
  { icon: Coffee, key: "amenity_coffee", descKey: "std_coffee_d" },
  { icon: ChefHat, key: "amenity_kitchen", descKey: "std_kitchen_d" },
  { icon: Sparkles, key: "std_quality", descKey: "std_quality_d" },
];

function RouteLine({ className = "mb-3" }) {
  return (
    <svg className={`route-line ${className}`} width="90" height="16" viewBox="0 0 90 16" aria-hidden="true">
      <circle cx="4" cy="8" r="3" />
      <path d="M9 8 C 30 -2, 60 18, 86 8" />
      <circle cx="86" cy="8" r="3" />
    </svg>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[var(--warm)]">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left group">
        <span className="font-medium text-[var(--ink)] text-sm md:text-base pr-4">{q}</span>
        <span className={`shrink-0 w-7 h-7 rounded-full border border-[var(--terra)] text-[var(--terra)] flex items-center justify-center transition-transform ${open ? "rotate-45" : ""}`}>+</span>
      </button>
      {open && (
        <div className="pb-5 text-[var(--muted)] text-sm leading-relaxed fade-in">{a}</div>
      )}
    </div>
  );
}

function BookingCta() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  return (
    <div className="bg-white shadow-[0_8px_64px_rgba(28,28,28,0.12)] flex flex-col md:flex-row items-center justify-between gap-4 px-6 py-6 md:py-7">
      <div className="text-center md:text-left">
        <p className="text-[10px] uppercase tracking-[0.15em] text-[var(--muted)] mb-1">
          {t("availability_prices")}
        </p>
        <p className="text-sm font-medium text-[var(--ink)]">
          {t("book_direct_note")}
        </p>
      </div>
      <button onClick={() => navigate("/apartments")}
        className="px-8 py-4 bg-[var(--terra)] hover:bg-[var(--terra-dark)] text-white text-xs font-bold uppercase tracking-[0.15em] transition-all duration-300 flex items-center justify-center gap-3 group shrink-0">
        {t("search_btn")} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
      </button>
    </div>
  );
}

export default function Home() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [hero, setHero] = useState(DEFAULT_HERO);
  const [gallery, setGallery] = useState(DEFAULT_GALLERY);
  const [cta, setCta] = useState(DEFAULT_GALLERY[2]);
  const [locImages, setLocImages] = useState({
    oberhausen: "https://images.unsplash.com/photo-1630699144035-c0f6311ec482?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    dinslaken: "https://images.unsplash.com/photo-1564078516393-cf04bd966897?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  });
  const [slideIndex, setSlideIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.title = "Atlas Apartment – Serviced Apartments in Oberhausen & Dinslaken";
    api.get("/settings").then(r => {
      if (r.data.hero_image) setHero(r.data.hero_image);
      if (r.data.gallery_images?.length) setGallery(r.data.gallery_images);
      if (r.data.cta_image) setCta(r.data.cta_image);
      setLocImages((prev) => ({
        oberhausen: r.data.loc_oberhausen_image || prev.oberhausen,
        dinslaken: r.data.loc_dinslaken_image || prev.dinslaken,
      }));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => setSlideIndex(p => (p + 1) % gallery.length), 5000);
    return () => clearInterval(t);
  }, [gallery.length]);

  const stats = [
    { value: "10+", label: t("stat_apartments") },
    { value: "2", label: t("stat_cities") },
    { value: "★ 4.9", label: t("stat_rating") },
    { value: "100%", label: t("stat_checkin") },
  ];

  return (
    <div className="bg-[var(--cream)]">

      {/* ═══ HERO ═══ */}
      <section className="relative h-screen min-h-[600px] overflow-hidden">
        <img src={hero} alt="Atlas Apartment – Serviced Apartments in Oberhausen und Dinslaken" width="1920" height="1080" fetchpriority="high" decoding="async"
          className="absolute inset-0 w-full h-full object-cover scale-[1.08]"
          style={{ transform: `translateY(${scrollY * 0.35}px) scale(1.08)` }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(28,28,28,0.3) 0%, rgba(28,28,28,0.15) 40%, rgba(28,28,28,0.7) 100%)" }} />
        <CursorSpotlight color="217,180,115" size={600} opacity={0.22} />

        {/* Centered title */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <p className="text-white/60 text-sm tracking-[0.15em] mb-6 fade-in">ruhrgebiet · deutschland</p>
          <h1 className="font-display text-5xl sm:text-7xl md:text-8xl lg:text-9xl text-white font-medium leading-[0.88] mb-6 fade-up" style={{ animationDelay: "0.1s" }}>
            Atlas Apartment
          </h1>
          <p className="text-white/70 text-sm md:text-base max-w-md fade-up" style={{ animationDelay: "0.25s" }}>
            {t("hero_sub")}
          </p>
          <MagneticButton onClick={() => navigate("/apartments")}
            className="btn-primary mt-8 fade-up" style={{ animationDelay: "0.4s" }}>
            {t("discover_apartments_btn")} <ArrowRight size={14} />
          </MagneticButton>
        </div>

        {/* Stats bar */}
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-5xl mx-auto px-4">
            {/* Booking CTA */}
            <div className="fade-up" style={{ animationDelay: "0.5s" }}>
              <BookingCta />
            </div>
            {/* Stats */}
            <div className="hidden md:flex justify-center gap-16 py-5">
              {stats.map((s, i) => (
                <div key={i} className="text-center">
                  <CountUp value={s.value} className="text-white font-display text-2xl font-medium" />
                  <div className="text-white/40 text-xs tracking-wide mt-0.5">{s.label.toLowerCase()}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-4 right-6 flex items-center gap-2 text-white/40 text-xs tracking-wide">
          <span>scroll</span>
          <ChevronDown size={12} className="animate-bounce" />
        </div>
      </section>

      {/* ═══ INTRO BAND ═══ */}
      <section className="bg-[var(--sage)] text-white py-5 overflow-hidden">
        <div className="flex marquee-track whitespace-nowrap gap-12 text-sm font-medium text-white/60">
          {[...Array(8)].map((_, i) => (
            <span key={i} className="flex items-center gap-12">
              <span>Oberhausen</span><span className="text-[var(--terra-light)]">•</span>
              <span>Dinslaken</span><span className="text-[var(--terra-light)]">•</span>
              <span>Self Check-in</span><span className="text-[var(--terra-light)]">•</span>
              <span>Bestpreis</span><span className="text-[var(--terra-light)]">•</span>
              <span>24/7 Support</span><span className="text-[var(--terra-light)]">•</span>
              <span>Ruhrgebiet</span><span className="text-[var(--terra-light)]">•</span>
            </span>
          ))}
        </div>
      </section>

      {/* ═══ APARTMENTS / XENTRA WIDGET ═══ */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <RouteLine />
              <span className="tag">{t("tag_our_apartments")}</span>
              <h2 className="font-display text-4xl md:text-6xl font-medium mt-2">{t("locations_title")}</h2>
            </div>
            <button onClick={() => navigate("/apartments")}
              className="btn-outline self-start md:self-auto shrink-0">
              {t("view_all")} <ArrowRight size={14} />
            </button>
          </div>

          <div className="mb-6"><PromoCard showButton={false} /></div>
          <div className="rounded-[28px] overflow-hidden border border-[var(--warm)] bg-white">
            <XentraWidget />
          </div>
        </div>
      </section>

      {/* ═══ FEATURES / STANDARDS (Grid-Stil wie stay-bright) ═══ */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <span className="tag">{t("tag_our_standards")}</span>
            <h2 className="font-display text-4xl md:text-5xl font-medium mt-3 mb-4">
              {t("standards_title")}
            </h2>
            <p className="text-[var(--muted)] text-base leading-relaxed">{t("standards_sub")}</p>
          </Reveal>

          <div className="grid sm:grid-cols-2 border border-[var(--sage-dark)] divide-y divide-[var(--sage-dark)] sm:divide-y-0">
            {FEATURES.map((f, i) => (
              <Reveal as="div" key={i} delay={i * 0.08}
                className={`sm:border-r sm:border-[var(--sage-dark)] ${i % 2 === 1 ? "sm:border-r-0" : ""} ${i >= 2 ? "sm:border-t sm:border-[var(--sage-dark)]" : ""}`}>
                <TiltCard className="bg-white p-8 md:p-10 h-full" maxTilt={6}>
                  <f.icon size={40} strokeWidth={1.2} className="text-[var(--sage-dark)] mb-5" />
                  <h3 className="font-display text-lg md:text-xl font-semibold uppercase tracking-wide text-[var(--sage-dark)] mb-2">
                    {t(f.key)}
                  </h3>
                  <p className="text-sm text-[var(--muted)] leading-relaxed">{t(f.descKey)}</p>
                </TiltCard>
              </Reveal>
            ))}
          </div>

          <div className="text-center mt-12">
            <button onClick={() => navigate("/apartments")} className="btn-primary">
              {t("book_now")} <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </section>

      {/* ═══ SAVINGS CALCULATOR ═══ */}
      <section className="py-8 md:py-16">
        <Reveal className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SavingsCalculator />
        </Reveal>
      </section>

      {/* ═══ GALLERY SLIDER ═══ */}
      <section className="py-20 md:py-28">
        <Reveal className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
          <RouteLine /><span className="tag">{t("tag_gallery")}</span>
          <h2 className="font-display text-4xl md:text-6xl font-light mt-2">{t("gallery_title")}</h2>
        </Reveal>
        <div className="relative overflow-hidden">
          {/* Main slider */}
          <div className="flex transition-transform duration-700 ease-[cubic-bezier(0.25,0.46,0.45,0.94)]"
            style={{ transform: `translateX(-${slideIndex * 100}%)` }}>
            {gallery.map((src, i) => (
              <div key={i} className="w-full shrink-0 h-[50vh] md:h-[70vh] px-4 sm:px-6 lg:px-8">
                <img src={src} alt={`Atlas Apartment – Einblick ${i + 1}`} width="900" height="1100" loading="lazy" decoding="async"
                  onClick={() => { setSlideIndex(i); setLightboxOpen(true); }}
                  className="w-full h-full object-cover rounded-[28px] cursor-zoom-in hover:brightness-95 transition-[filter]" />
              </div>
            ))}
          </div>
          {/* Slide controls */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
            {gallery.map((_, i) => (
              <button key={i} onClick={() => setSlideIndex(i)}
                className={`transition-all duration-300 ${slideIndex === i ? "w-8 h-1.5 bg-white" : "w-1.5 h-1.5 bg-white/40 rounded-full"}`} />
            ))}
          </div>
          {/* CTA overlay */}
          <div className="absolute bottom-6 right-6 hidden md:block">
            <button onClick={() => navigate("/apartments")} className="btn-primary bg-white/10 backdrop-blur-sm hover:bg-white/20">
              {t("all_apartments_btn")} <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </section>

      {/* ═══ LOCATIONS ═══ */}
      <section className="py-20 md:py-28 bg-[var(--warm)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <RouteLine className="mb-3 mx-auto" /><span className="tag">{t("tag_locations")}</span>
            <h2 className="font-display text-4xl md:text-6xl font-light mt-2">
              {t("discover_ruhr")}
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                city: "Oberhausen",
                count: "8",
                descKey: "loc_oberhausen_desc",
                img: locImages.oberhausen,
              },
              {
                city: "Dinslaken",
                count: "2",
                descKey: "loc_dinslaken_desc",
                img: locImages.dinslaken,
              },
            ].map(loc => (
              <TiltCard as="div" key={loc.city} maxTilt={5} glare={false}
                className="group cursor-pointer overflow-hidden aspect-[16/10] rounded-[28px]"
                onClick={() => navigate(`/apartments?city=${loc.city}`)}>
                <img src={loc.img} alt={loc.city} width="800" height="1000" loading="lazy" decoding="async" className="img-zoom w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)]/80 via-[var(--ink)]/20 to-transparent" />
                <div className="absolute inset-0 p-8 flex flex-col justify-end">
                  <span className="text-[var(--terra-light)] text-sm">
                    {loc.count} {t("stat_apartments")}
                  </span>
                  <h3 className="font-display text-white text-4xl md:text-5xl font-light mt-1 mb-3">{loc.city}</h3>
                  <p className="text-white/60 text-sm leading-relaxed max-w-sm opacity-0 group-hover:opacity-100 transition-opacity duration-400">
                    {t(loc.descKey)}
                  </p>
                  <div className="flex items-center gap-2 text-white text-xs font-medium mt-4 group-hover:gap-3 transition-all">
                    {t("view_apartments_btn")} <ArrowRight size={13} />
                  </div>
                </div>
              </TiltCard>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ TESTIMONIALS ═══ */}
      <section className="py-20 md:py-28 overflow-hidden">
        <Reveal className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <RouteLine /><span className="tag">{t("tag_reviews")}</span>
              <h2 className="font-display text-4xl md:text-6xl font-light mt-2">{t("testi_title")}</h2>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {[...Array(5)].map((_, i) => <Star key={i} size={14} className="fill-[var(--gold)] text-[var(--gold)]" />)}
              <span className="text-[var(--muted)] text-sm ml-1">4.9 / 5</span>
            </div>
          </div>
        </Reveal>

        {/* Horizontal scroll */}
        <div className="flex gap-5 overflow-x-auto hide-scrollbar px-4 sm:px-6 lg:px-8 pb-4">
          {[...TESTIMONIALS, ...TESTIMONIALS.slice(0, 2)].map((item, i) => (
            <div key={i} className="shrink-0 w-72 md:w-80 bg-white p-7 rounded-[24px] border-t-2 border-[var(--terra)] hover-lift">
              <div className="flex gap-1 mb-5">
                {[...Array(item.rating)].map((_, j) => <Star key={j} size={12} className="fill-[var(--terra)] text-[var(--terra)]" />)}
              </div>
              <p className="font-display text-xl font-light text-[var(--ink)] leading-relaxed mb-6 italic">
                "{t(item.key)}"
              </p>
              <div className="flex items-center gap-3 pt-4 border-t border-[var(--warm)]">
                <div className="w-8 h-8 bg-[var(--sage)] text-white rounded-full flex items-center justify-center text-sm font-medium">
                  {item.name[0]}
                </div>
                <div>
                  <p className="text-sm font-semibold text-[var(--ink)]">{item.name}</p>
                  <p className="text-xs text-[var(--muted)]">{item.city}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ ÜBER UNS ═══ */}
      <section className="py-20 md:py-28 bg-[var(--warm)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <Reveal>
              <RouteLine /><span className="tag">{t("tag_your_hosts")}</span>
              <h2 className="font-display text-4xl md:text-5xl font-light mt-3 mb-6">{t("about_title")}</h2>
              <p className="text-[var(--muted)] leading-relaxed mb-4">{t("about_text1")}</p>
              <p className="text-[var(--muted)] leading-relaxed mb-10">{t("about_text2")}</p>
              <div className="space-y-3 text-sm">
                <a href="tel:+4915156634125" className="flex items-center gap-3 text-[var(--ink)] hover:text-[var(--terra)] transition-colors">
                  <span className="w-9 h-9 rounded-full border border-[var(--ink)]/20 flex items-center justify-center text-xs">📞</span>
                  +49 151 56634125
                </a>
                <a href="mailto:info@atlas-apartment.de" className="flex items-center gap-3 text-[var(--ink)] hover:text-[var(--terra)] transition-colors">
                  <span className="w-9 h-9 rounded-full border border-[var(--ink)]/20 flex items-center justify-center text-xs">✉</span>
                  info@atlas-apartment.de
                </a>
                <div className="flex items-center gap-3 text-[var(--muted)]">
                  <span className="w-9 h-9 rounded-full border border-[var(--ink)]/20 flex items-center justify-center text-xs">📍</span>
                  Ursulastr. 7c, 46537 Dinslaken
                </div>
              </div>
            </Reveal>
            {/* Stats */}
            <Reveal delay={0.15} className="grid grid-cols-3 gap-3">
              {[
                ["10+", t("stat_apartments"), true],
                ["2", t("stat_cities"), true],
                ["★ 4.9", t("stat_rating"), true],
                ["55–90", "m² " + t("stat_size").replace("m² ",""), false],
                ["100%", t("stat_checkin"), true],
                ["24/7", t("stat_support"), false],
              ].map(([val, label, animate]) => (
                <div key={label} className="bg-[var(--warm)] p-6 text-center rounded-[18px] hover:bg-[var(--terra)] hover:text-white transition-colors group">
                  {animate ? (
                    <CountUp value={val} className="font-display text-3xl font-light mb-1 text-[var(--ink)] group-hover:text-white block" />
                  ) : (
                    <div className="font-display text-3xl font-light mb-1 text-[var(--ink)] group-hover:text-white">{val}</div>
                  )}
                  <div className="text-xs text-[var(--muted)] group-hover:text-white/70">{label}</div>
                </div>
              ))}
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ FAQ ═══ */}
      <section className="py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16">
            <Reveal>
              <RouteLine /><span className="tag">faq</span>
              <h2 className="font-display text-4xl md:text-5xl font-light mt-3 mb-6">
                {t("faq_title")}
              </h2>
              <p className="text-[var(--muted)] mb-8 leading-relaxed">
                {t("faq_sub")}
              </p>
              <a href="mailto:info@atlas-apartment.de" className="btn-outline">
                ✉ {t("contact_us_btn")}
              </a>
            </Reveal>
            <Reveal delay={0.15} className="divide-y divide-[var(--warm)]">
              {[
                { q: t("faq_q1"), a: t("faq_a1") },
                { q: t("faq_q2"), a: t("faq_a2") },
                { q: t("faq_q3"), a: t("faq_a3") },
                { q: t("faq_q4"), a: t("faq_a4") },
                { q: t("faq_q5"), a: t("faq_a5") },
              ].map((f, i) => <FaqItem key={i} q={f.q} a={f.a} />)}
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="relative py-28 md:py-48 overflow-hidden">
        <img src={cta || hero} alt="" width="1920" height="800" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[var(--sage-dark)]/80" />
        <Reveal className="relative max-w-3xl mx-auto px-4 text-center text-white">
          <span className="text-[var(--gold)] text-sm mb-6 block">⚡ bestpreis-garantie</span>
          <h2 className="font-display text-5xl sm:text-6xl md:text-8xl font-light leading-[0.9] mb-8">
            {t("cta_title")}
          </h2>
          <p className="text-white/50 text-base md:text-lg mb-12 max-w-lg mx-auto leading-relaxed">
            {t("cta_sub")}
          </p>
          <MagneticButton onClick={() => navigate("/apartments")} className="btn-primary text-sm px-10 py-5">
            {t("cta_btn")} <ArrowRight size={16} />
          </MagneticButton>
        </Reveal>
      </section>

      {lightboxOpen && (
        <PhotoLightbox images={gallery} index={slideIndex} onClose={() => setLightboxOpen(false)} title="Atlas Apartment" />
      )}
    </div>
  );
}
